/**
 * StationDetailPage - Detailansicht einer Station
 * 
 * Sektionen:
 * - Grunddaten (Name, Standort)
 * - Konfiguration (Typ)
 * - Pairing/Setup (Info + Link zum Pairing-Flow)
 * - Hinweise (Snapshot-Daten)
 * - Gefahrenzone (Deaktivieren)
 * 
 * UX-Leitlinie:
 * - Kein Modal für Edit, nur für Confirm
 * - Create erfolgt via Pairing-Flow auf dem Zielgerät
 * - Pairing als eigene Section (kein Modal)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Monitor, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

type StationType = 'WSA' | 'VORARBEITER' | 'ADMIN';

interface Station {
  id: string;
  name: string | null;
  type: StationType;
  location: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  registeredBy: { displayName: string } | null;
}

interface FormData {
  name: string;
  type: StationType;
  location: string;
}

const TYPE_LABELS: Record<StationType, string> = {
  WSA: 'WSA-Erfassung',
  VORARBEITER: 'Vorarbeiter',
  ADMIN: 'Admin',
};

const TYPE_COLORS: Record<StationType, string> = {
  WSA: 'bg-blue-100 text-blue-800',
  VORARBEITER: 'bg-green-100 text-green-800',
  ADMIN: 'bg-orange-100 text-orange-800',
};

export default function StationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [station, setStation] = useState<Station | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>({ name: '', type: 'WSA', location: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '–';
    return new Date(dateStr).toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Load station details
  const loadStation = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/stations/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStation(data);
        setFormData({
          name: data.name || '',
          type: data.type,
          location: data.location || '',
        });
      } else if (res.status === 404) {
        navigate('/admin/stations');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Station:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStation();
  }, [id]);

  // Save
  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`/api/admin/stations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name.trim() || null,
          type: formData.type,
          location: formData.location.trim() || null,
        }),
      });

      if (res.ok) {
        loadStation();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete
  const handleDelete = async () => {
    if (!id) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/stations/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/stations');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Fehler beim Deaktivieren');
        setDeleteConfirmOpen(false);
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!station) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Station nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to="/admin/stations" 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Übersicht
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Monitor className="w-6 h-6" />
              {station.name || 'Unbenannte Station'}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-1 text-xs rounded ${TYPE_COLORS[station.type]}`}>
                {TYPE_LABELS[station.type]}
              </span>
              <span className="text-xs text-slate-400 font-mono">{station.id.slice(0, 8)}...</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadStation()}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. WSA-Terminal 1"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Standort</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="z.B. Halle A"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </section>

          {/* Konfiguration */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Konfiguration</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Stationstyp</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as StationType })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="WSA">WSA-Erfassung</option>
                <option value="VORARBEITER">Vorarbeiter</option>
              </select>
            </div>

            <div className="text-xs text-slate-500 space-y-1">
              <p><strong>ID:</strong> <span className="font-mono">{station.id}</span></p>
              <p><strong>Registriert von:</strong> {station.registeredBy?.displayName || '–'}</p>
              <p><strong>Erstellt:</strong> {formatDate(station.createdAt)}</p>
              <p><strong>Zuletzt gesehen:</strong> {formatDate(station.lastSeenAt)}</p>
            </div>
          </section>

          {/* Pairing / Setup */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Pairing / Setup</h2>
            
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="mb-2">
                  Diese Station wurde via <strong>Pairing-Flow</strong> registriert. 
                  Um eine neue Station zu koppeln, navigieren Sie auf dem Zielgerät zu:
                </p>
                <code className="block bg-blue-100 px-3 py-2 rounded font-mono text-blue-900">
                  /pair
                </code>
                <p className="mt-2 text-blue-700">
                  Der Pairing-Code wird vom System generiert und muss auf dem Admin-Gerät bestätigt werden.
                </p>
              </div>
            </div>

            <a
              href="/pair"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <ExternalLink className="w-4 h-4" />
              Pairing-Seite öffnen (neues Fenster)
            </a>
          </section>

          {/* Hinweise */}
          <section className="bg-slate-50 rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-700">Hinweise</h2>
            <p className="text-sm text-slate-600">
              Änderungen wirken sich nicht auf bereits erfasste Daten aus (Snapshots).
            </p>
          </section>
        </div>

        {/* Delete Section */}
        <section className="bg-white rounded-lg border border-red-200 p-6">
          <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
          <p className="text-sm text-slate-600 mb-4">
            Das Deaktivieren einer Station entfernt sie aus dem System. 
            Das Gerät muss erneut gekoppelt werden, um wieder aktiv zu sein.
          </p>
          <button
            onClick={() => setDeleteConfirmOpen(true)}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Station deaktivieren
          </button>
        </section>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Station deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{station.name || 'Unbenannt'}"</strong> ({TYPE_LABELS[station.type]}) wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Das Gerät muss erneut gekoppelt werden, um wieder aktiv zu sein.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
